import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader, EmptyState } from "@/components/Layout";
import { ClientPanel, type ClientFormValue } from "@/components/forms";
import { Button, ErrorNote, useCanWrite } from "@/components/ui";
import { DeleteForever } from "@/components/delete";
import { api, errorMessage } from "@/lib/api";
import { date, dateTime, lots } from "@/lib/format";
import { useClients, useRefreshDirectory } from "@/lib/queries";
import type { ClientListItem, Closure } from "@/lib/types";

/**
 * GLI ARCHIVIATI, e da qui si torna indietro.
 *
 * **Se non c'è niente, non c'è la sezione** — non un riquadro vuoto che dice
 * «nessun archiviato». È la stessa regola del contatore degli eventi: una cosa
 * sempre accesa che non dice niente, dopo due giorni non la si vede più.
 *
 * Sta in fondo alla pagina dei clienti e non in una voce di menu sua: è
 * un'azione amministrativa rara, e una voce di menu permanente per una cosa che
 * capita tre volte l'anno occupa spazio tutti gli altri giorni.
 */
function Archiviati({ canWrite }: { canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const q = useQuery({ queryKey: ["archived"], queryFn: () => api.archived() });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clients = q.data?.clients ?? [];
  const accounts = q.data?.accounts ?? [];
  if (!clients.length && !accounts.length) return null;

  const ripristina = async (che: "cliente" | "conto", id: string) => {
    setBusy(id);
    setError(null);
    try {
      await (che === "cliente" ? api.clients.restore(id) : api.accounts.restore(id));
      await q.refetch();
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="border-t border-line px-6 py-5">
      <h2 className="text-sm font-semibold text-ink">Archiviati</h2>
      <p className="mt-0.5 text-xs text-muted">
        Le righe archiviate non compaiono da nessun'altra parte, ma ci sono ancora: da qui tornano attive.
      </p>

      <div className="mt-3 space-y-1.5">
        {clients.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-faint">Cliente</span>
            <span className="text-ink">{c.name}</span>
            <span className="text-muted">{c.email}</span>
            {canWrite && (
              <Button size="sm" variant="ghost" disabled={busy === c.id} onClick={() => void ripristina("cliente", c.id)}>
                {busy === c.id ? "…" : "Ripristina"}
              </Button>
            )}
          </div>
        ))}
        {accounts.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-faint">Conto</span>
            <span className="text-ink">{a.label}</span>
            <span className="font-mono text-xs text-muted">{a.mt5Login}</span>
            <span className="text-muted">{a.server.name}</span>
            {/*
              * Il conto di un cliente archiviato non si può ripristinare, e
              * dirlo QUI è meglio che lasciarglielo scoprire da un rifiuto: il
              * pulsante c'è comunque, perché il motivo lo dà il backend con
              * l'ordine da seguire.
              */}
            {a.client.isArchived && <span className="text-xs text-warn">prima il cliente {a.client.name}</span>}
            {canWrite && (
              <Button size="sm" variant="ghost" disabled={busy === a.id} onClick={() => void ripristina("conto", a.id)}>
                {busy === a.id ? "…" : "Ripristina"}
              </Button>
            )}
          </div>
        ))}
      </div>

      {error && <p className="mt-2 text-xs text-loss">{error}</p>}
    </section>
  );
}

/**
 * GLI ELIMINATI: stessa pagina degli archiviati, altra promessa.
 *
 * Un archiviato tiene il suo slot unico e torna sempre; un eliminato lo libera,
 * e il ritorno puo' essere rifiutato: il messaggio del backend dice con quale
 * riga attiva va in conflitto, e qui si mostra com'e' arrivato. Si elencano solo
 * le radici: il resto torna insieme a loro.
 */
function Eliminati({ canWrite }: { canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const q = useQuery({ queryKey: ["deleted"], queryFn: () => api.deleted(), enabled: canWrite });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ id: string; message: string } | null>(null);

  const righe = [
    ...(q.data?.accounts ?? []).map((a) => ({
      id: a.id, kind: "accounts" as const, tipo: "Conto", nome: a.label, dettaglio: `${a.mt5Login} · ${a.server.name}`, deletedAt: a.deletedAt, chi: a.deletedBy, closures: a.closures,
    })),
    ...(q.data?.cycles ?? []).map((c) => ({
      id: c.id, kind: "cycles" as const, tipo: "Ciclo", nome: c.label, dettaglio: c.client.name, deletedAt: c.deletedAt, chi: c.deletedBy, closures: c.closures,
    })),
    ...(q.data?.sessions ?? []).map((f) => ({
      id: f.id, kind: "sessions" as const, tipo: "Fase", nome: `Fase ${f.phase} · ${f.canonicalSymbol}`, dettaglio: `${f.cycle.label} · ${f.cycle.client.name}`, deletedAt: f.deletedAt, chi: f.deletedBy, closures: f.closures,
    })),
  ].sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));
  if (!righe.length) return null;

  const ripristina = async (r: (typeof righe)[number]) => {
    setBusy(r.id);
    setError(null);
    try {
      await api.undelete(r.kind, r.id);
      await q.refetch();
      await refresh();
    } catch (e) {
      setError({ id: r.id, message: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="border-t border-line px-6 py-5">
      <h2 className="text-sm font-semibold text-ink">Eliminati</h2>
      <p className="mt-0.5 text-xs text-muted">
        Spariti da tutte le schermate, con quello che portavano con sé. Il ripristino li riporta insieme, ma non riapre le posizioni chiuse; se il loro numero è stato ripreso da un'altra riga, viene rifiutato.
      </p>
      <div className="mt-3 space-y-1.5">
        {righe.map((r) => (
          <div key={r.id}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-faint">{r.tipo}</span>
              <span className="text-ink">{r.nome}</span>
              <span className="text-muted">{r.dettaglio}</span>
              <span className="text-xs text-muted">eliminato il {dateTime(r.deletedAt)}</span>
              <span className="text-xs text-muted">da {r.chi ?? "utente non più leggibile"}</span>
              <EsitoChiusure closures={r.closures} />
              <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => void ripristina(r)}>
                {busy === r.id ? "…" : "Ripristina"}
              </Button>
            </div>
            {error?.id === r.id && (
              <p role="alert" className="mt-1 text-xs text-loss">
                {error.message}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/** Esito delle chiusure a mercato. Rosso se qualcosa è rimasto aperto; nullo = non registrato, non «nessuna». */
function EsitoChiusure({ closures }: { closures: Closure[] | null }) {
  if (closures === null) return <span className="text-xs text-faint">chiusure: non registrate</span>;
  if (!closures.length) return <span className="text-xs text-muted">nessuna posizione a mercato</span>;
  const rimaste = closures.filter((c) => c.esito === "RIMASTA_APERTA");
  if (!rimaste.length) return <span className="text-xs text-muted">{closures.length === 1 ? "1 posizione chiusa" : `${closures.length} posizioni chiuse`}</span>;
  return (
    <span className="text-xs text-loss">
      {rimaste.length === 1 ? "1 posizione rimasta aperta" : `${rimaste.length} posizioni rimaste aperte`}:{" "}
      {rimaste.map((c) => `${c.accountLabel} ${c.symbol} ${lots(c.lots)} lotti, ticket ${c.ticket}${c.motivo ? ` (${c.motivo})` : ""}`).join("; ")}
    </span>
  );
}

export function Clients() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const clients = useClients();
  const [panel, setPanel] = useState<{ open: boolean; client: ClientFormValue | null }>({ open: false, client: null });

  const openNew = () => setPanel({ open: true, client: null });

  return (
    <>
      <PageHeader
        title="Clienti"
        subtitle="Anagrafica, conti, e il proxy e il CID con cui escono"
        actions={canWrite && <Button variant="primary" onClick={openNew}>Nuovo cliente</Button>}
      />

      {clients.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : clients.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(clients.error)}</ErrorNote>
        </div>
      ) : !clients.data?.length ? (
        <EmptyState
          title="Nessun cliente"
          hint="Crea il primo cliente col suo proxy e il suo CID, poi aggiungi i suoi due conti MT5: uno prop e uno broker. Proxy e CID stanno sul cliente, e valgono per tutti i suoi conti."
          action={canWrite && <Button variant="primary" onClick={openNew}>Nuovo cliente</Button>}
        />
      ) : (
        <div className="overflow-x-auto px-6 py-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-faint">
                <th className="py-2 pr-4 font-medium">Nome</th>
                <th className="py-2 pr-4 font-medium">Email</th>
                <th className="py-2 pr-4 font-medium">Conti</th>
                <th className="py-2 pr-4 font-medium">Uscita</th>
                <th className="py-2 pr-4 font-medium">Connessione</th>
                <th className="py-2 pr-4 font-medium">Creato</th>
                <th className="py-2 font-medium">
                  <span className="sr-only">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {clients.data.map((c) => (
                <ClientRow
                  key={c.id}
                  client={c}
                  canWrite={canWrite}
                  onOpen={() => navigate(`/clienti/${c.id}`)}
                  onEdit={() => setPanel({ open: true, client: c })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Archiviati canWrite={canWrite} />
      <Eliminati canWrite={canWrite} />

      <ClientPanel
        open={panel.open}
        client={panel.client}
        onClose={() => setPanel({ open: false, client: null })}
        onSaved={(id) => !panel.client && navigate(`/clienti/${id}`)}
      />
    </>
  );
}

function ClientRow({
  client,
  canWrite,
  onOpen,
  onEdit,
}: {
  client: ClientListItem;
  canWrite: boolean;
  onOpen: () => void;
  onEdit: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const props = client.accounts.filter((a) => a.role === "PROP").length;
  const brokers = client.accounts.filter((a) => a.role === "BROKER").length;
  const connectable = client.accounts.filter((a) => a.role !== "NONE");
  const connected = client.accounts.filter((a) => a.isConnected).length;

  /** In sequenza, non in parallelo: Mirror apre una sessione MT5 per conto. */
  const bulk = async (kind: "connect" | "disconnect") => {
    setBusy(kind);
    setMessage(null);
    const targets = connectable.filter((a) => (kind === "connect" ? !a.isConnected : a.isConnected));
    const failures: string[] = [];
    for (const a of targets) {
      try {
        await (kind === "connect" ? api.accounts.connect(a.id) : api.accounts.disconnect(a.id));
      } catch (e) {
        failures.push(`${a.label}: ${errorMessage(e)}`);
      }
    }
    await refresh();
    setBusy(null);
    if (!targets.length) setMessage(kind === "connect" ? "Tutti i conti con un ruolo sono già connessi." : "Nessun conto connesso.");
    else if (failures.length) setMessage(failures.join(" · "));
  };

  const archive = async () => {
    setBusy("archive");
    setMessage(null);
    try {
      await api.clients.archive(client.id);
      await refresh();
    } catch (e) {
      setMessage(errorMessage(e));
      setBusy(null);
      setConfirmArchive(false);
    }
  };

  return (
    <>
      <tr className="border-b border-line/60 hover:bg-raised/40">
        <td className="py-2.5 pr-4">
          <Link to={`/clienti/${client.id}`} className="font-medium text-ink hover:underline">
            {client.name}
          </Link>
        </td>
        <td className="py-2.5 pr-4 text-muted">{client.email}</td>
        <td className="py-2.5 pr-4">
          {client.accounts.length === 0 ? (
            <span className="text-faint">Nessun conto</span>
          ) : (
            <span className="flex items-center gap-1.5">
              <span className="text-ink">{client.accounts.length}</span>
              <span className="text-faint">·</span>
              <span className="text-prop">{props} prop</span>
              <span className="text-faint">·</span>
              <span className="text-broker">{brokers} broker</span>
            </span>
          )}
        </td>
        {/* Da dove esce: paese e citta' si leggono senza svelare la password,
            perche' non ci stanno piu' dentro. */}
        <td className="py-2.5 pr-4">
          {client.defaultProxy ? (
            <span className="inline-flex items-center gap-1.5">
              <span
                className={`h-2 w-2 rounded-full ${client.defaultProxy.lastCheckOk === null ? "bg-faint" : client.defaultProxy.lastCheckOk ? "bg-gain" : "bg-loss"}`}
                title={client.defaultProxy.lastCheckAt ? `Ultimo controllo ${date(client.defaultProxy.lastCheckAt)}` : "Mai controllato"}
                aria-hidden
              />
              <span className="text-ink">
                {[client.defaultProxy.city, client.defaultProxy.country].filter(Boolean).join(", ") || `${client.defaultProxy.host}:${client.defaultProxy.port}`}
              </span>
            </span>
          ) : (
            <span className="text-warn" title="Senza proxy il conto prop non si connette">
              Nessun proxy
            </span>
          )}
        </td>
        <td className="py-2.5 pr-4">
          {client.accounts.length === 0 ? (
            <span className="text-faint">—</span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span
                className={`h-2 w-2 rounded-full ${connected === 0 ? "bg-faint" : connected === client.accounts.length ? "bg-gain" : "bg-warn"}`}
                aria-hidden
              />
              <span className={connected ? "text-ink" : "text-muted"}>
                {connected} di {client.accounts.length} connessi
              </span>
            </span>
          )}
        </td>
        <td className="py-2.5 pr-4 text-muted">{date(client.createdAt)}</td>
        <td className="py-2.5">
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={onOpen}>
              Apri
            </Button>
            {canWrite && (
              <>
                <Button size="sm" variant="ghost" onClick={onEdit}>
                  Modifica
                </Button>
                <Button size="sm" variant="ghost" disabled={!!busy || connectable.length === 0} onClick={() => void bulk("connect")}>
                  {busy === "connect" ? "Connessione…" : "Connetti tutti"}
                </Button>
                <Button size="sm" variant="ghost" disabled={!!busy || connected === 0} onClick={() => void bulk("disconnect")}>
                  {busy === "disconnect" ? "Disconnessione…" : "Disconnetti tutti"}
                </Button>
                {confirmArchive ? (
                  <>
                    <Button size="sm" variant="danger" disabled={!!busy} onClick={() => void archive()}>
                      {busy === "archive" ? "Archiviazione…" : "Conferma archiviazione"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmArchive(false)}>
                      Annulla
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setConfirmArchive(true)}>
                      Archivia
                    </Button>
                    {/*
                      * Accanto ad «Archivia», non al suo posto: archiviare
                      * conserva i riferimenti, eliminare li toglie di mezzo. La
                      * differenza la spiega la frase che compare quando si
                      * chiede, non un'etichetta.
                      */}
                    <DeleteForever kind="cliente" id={client.id} label={client.name} size="sm" />
                  </>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
      {message && (
        <tr className="border-b border-line/60">
          <td colSpan={7} className="pb-2.5 text-xs text-loss">
            {message}
          </td>
        </tr>
      )}
    </>
  );
}
